// Admin-specific formatting: Decimal-string money and large counters.
// Dates reuse the shared formatters (shared/format) — one source of truth.
import { i18n } from '../../../i18n/index.js';

import { formatDate, formatDateTime } from '../../../shared/format/index.js';

export { formatDate, formatDateTime };

function intlLocale(): string {
  return i18n.locale === 'en' ? 'en-US' : 'ru-RU';
}

/**
 * LLM spend from the API comes as a Decimal-serialized string. Two decimals
 * (not the shared formatMoney's 0): platform spend is watched precisely,
 * and four-decimal aggregation noise is rounded away.
 */
export function formatAdminSpend(amount: string | number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(intlLocale(), {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Counter with locale digit grouping (tokens, messages, users). */
export function formatAdminCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(intlLocale()).format(value);
}

/**
 * Plan price from integer USD cents (owner decision 2026-10-20: $20/$50
 * subscriptions). `formatMoney` handles RUB amounts; plans are USD.
 */
export function formatPlanPrice(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat(intlLocale(), {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * Plan storage limit from a BigInt-serialized byte string. Input and display
 * use GiB steps labeled GB (the owner-facing simplification: seeds are exact
 * 1/5 GiB); the DB keeps exact bytes.
 */
export function formatPlanStorage(bytes: string | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return '—';
  const gib = value / 2 ** 30;
  if (gib < 1) {
    return new Intl.NumberFormat(intlLocale(), {
      style: 'unit',
      unit: 'megabyte',
      maximumFractionDigits: 0,
    }).format(value / 2 ** 20);
  }
  return new Intl.NumberFormat(intlLocale(), {
    style: 'unit',
    unit: 'gigabyte',
    maximumFractionDigits: 1,
  }).format(gib);
}
